/**
 * TicketAssignmentModal
 *
 * Modal with drag-and-drop interface for manually assigning attendees
 * to PC (Producer Circle) or EB (Employee Benefits) sections.
 * Assignments persist across report uploads using stable event keys.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiClient } from '@shared/services/apiClient';
import { useNotificationStore } from '@shared/store/notificationStore';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import type { HMCEvent } from '../hmcEventsStore';
import { parseAttendance } from '../utils/attendanceParser';
import { parseLocalDate } from '../utils/dateUtils';

// Types
interface Assignment {
  attendee_name: string;
  section: 'PC' | 'EB' | 'unassigned';
  tickets: number;
}

interface TicketAssignmentModalProps {
  event: HMCEvent;
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

// Generate stable event key for persistence
const generateEventKey = (event: HMCEvent): string => {
  const eventType = (event.event_type || 'general').toLowerCase();
  const eventDate = event.event_date?.split('T')[0] || 'unknown';
  return `${eventType}_${eventDate}`;
};

// Draggable attendee card component
const AttendeeCard: React.FC<{
  assignment: Assignment;
  isDragging?: boolean;
}> = ({ assignment, isDragging }) => {
  const sectionColors = {
    PC: 'border-purple-500 bg-purple-500/10',
    EB: 'border-brand-500 bg-blue-500/10',
    unassigned: 'border-gray-500 bg-gray-500/10',
  };

  return (
    <div
      className={`
        px-3 py-2 rounded-lg border cursor-grab
        ${sectionColors[assignment.section]}
        ${isDragging ? 'opacity-50 scale-105 shadow-lg' : ''}
        transition-all duration-150
      `}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-white text-sm font-medium truncate">
          {assignment.attendee_name}
        </span>
        <span className="text-xs text-dark-muted bg-dark-bg px-1.5 py-0.5 rounded shrink-0">
          {assignment.tickets} ticket{assignment.tickets !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

// Sortable wrapper for attendee cards
const SortableAttendeeCard: React.FC<{
  assignment: Assignment;
}> = ({ assignment }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: assignment.attendee_name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <AttendeeCard assignment={assignment} isDragging={isDragging} />
    </div>
  );
};

// Mobile-friendly attendee card with tap buttons
const MobileAttendeeCard: React.FC<{
  assignment: Assignment;
  section: 'PC' | 'EB' | 'unassigned';
  onMove: (attendeeName: string, toSection: 'PC' | 'EB' | 'unassigned') => void;
}> = ({ assignment, section, onMove }) => {
  const sectionColors = {
    PC: 'border-purple-500 bg-purple-500/10',
    EB: 'border-brand-500 bg-blue-500/10',
    unassigned: 'border-gray-500 bg-gray-500/10',
  };

  return (
    <div
      className={`
        px-2 py-2 rounded-lg border
        ${sectionColors[assignment.section]}
      `}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="flex-1 min-w-0">
          <span className="text-white text-xs font-medium truncate block">
            {assignment.attendee_name}
          </span>
          <span className="text-[10px] text-dark-muted">
            {assignment.tickets} ticket{assignment.tickets !== 1 ? 's' : ''}
          </span>
        </div>
        {/* Tap-to-move buttons */}
        <div className="flex gap-1 shrink-0">
          {section !== 'PC' && (
            <button
              onClick={() => onMove(assignment.attendee_name, 'PC')}
              className="px-2 py-1 bg-purple-600 text-white rounded text-[10px] font-bold min-h-[28px] active:scale-95 transition-transform"
            >
              PC
            </button>
          )}
          {section !== 'EB' && (
            <button
              onClick={() => onMove(assignment.attendee_name, 'EB')}
              className="px-2 py-1 bg-brand-600 text-white rounded text-[10px] font-bold min-h-[28px] active:scale-95 transition-transform"
            >
              EB
            </button>
          )}
          {section !== 'unassigned' && (
            <button
              onClick={() => onMove(assignment.attendee_name, 'unassigned')}
              className="px-1.5 py-1 bg-dark-surface/50 text-white rounded text-[10px] font-bold min-h-[28px] active:scale-95 transition-transform"
              title="Remove assignment"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Droppable section column
const SectionColumn: React.FC<{
  title: string;
  section: 'PC' | 'EB' | 'unassigned';
  assignments: Assignment[];
  capacity?: number;
  color: string;
  /** Mobile mode - use tap buttons instead of drag */
  isMobile?: boolean;
  /** Handler for mobile tap-to-move */
  onMoveAssignment?: (attendeeName: string, toSection: 'PC' | 'EB' | 'unassigned') => void;
}> = ({ title, section, assignments, capacity, color, isMobile, onMoveAssignment }) => {
  const totalTickets = assignments.reduce((sum, a) => sum + a.tickets, 0);

  // Make this column a drop target
  const { setNodeRef, isOver } = useDroppable({
    id: section,
  });

  return (
    <div className="flex-1 min-w-0 sm:min-w-[200px] sm:max-w-[300px]">
      {/* Column header */}
      <div className={`px-2 sm:px-3 py-2 rounded-t-lg ${color} border-b border-dark-border/30`}>
        <div className="flex items-center justify-between">
          <span className="font-semibold text-white text-sm sm:text-base">{title}</span>
          {capacity !== undefined && (
            <span className="text-xs text-dark-text">
              {totalTickets}/{capacity}
            </span>
          )}
          {capacity === undefined && (
            <span className="text-xs text-dark-text">
              {assignments.length}
            </span>
          )}
        </div>
      </div>

      {/* Droppable area */}
      <div
        ref={setNodeRef}
        className={`
          min-h-[100px] sm:min-h-[200px] max-h-[180px] sm:max-h-[400px] overflow-y-auto
          bg-dark-surface border border-t-0 border-dark-border/30 rounded-b-lg
          p-1.5 sm:p-2 space-y-1 sm:space-y-2
          ${isOver ? 'ring-2 ring-brand-500 bg-brand-500/5' : ''}
          transition-all duration-150
        `}
      >
        {isMobile && onMoveAssignment ? (
          // Mobile: Use tap buttons instead of drag
          <>
            {assignments.map((assignment) => (
              <MobileAttendeeCard
                key={assignment.attendee_name}
                assignment={assignment}
                section={section}
                onMove={onMoveAssignment}
              />
            ))}
          </>
        ) : (
          // Desktop: Use drag and drop
          <SortableContext
            items={assignments.map((a) => a.attendee_name)}
            strategy={verticalListSortingStrategy}
          >
            {assignments.map((assignment) => (
              <SortableAttendeeCard
                key={assignment.attendee_name}
                assignment={assignment}
              />
            ))}
          </SortableContext>
        )}

        {assignments.length === 0 && (
          <div className={`h-full flex items-center justify-center text-xs sm:text-sm py-4 sm:py-8 ${isOver ? 'text-brand-400' : 'text-dark-muted'}`}>
            {isOver ? 'Release here' : isMobile ? 'Tap buttons to move' : 'Drop attendees here'}
          </div>
        )}
      </div>
    </div>
  );
};

// Main modal component
export const TicketAssignmentModal: React.FC<TicketAssignmentModalProps> = ({
  event,
  isOpen,
  onClose,
  onSave,
}) => {
  const { addNotification } = useNotificationStore();
  const isMobile = useIsMobile();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Calculate capacity
  const pcCapacity = 5; // Half of 10 typical suite tickets
  const ebCapacity = 5;

  // Parse current attendance from event
  const parsedAttendance = useMemo(() => {
    return parseAttendance(event.special_event);
  }, [event.special_event]);

  // Event key for API
  const eventKey = useMemo(() => generateEventKey(event), [event]);

  // Load saved assignments on mount (with memory fallback)
  useEffect(() => {
    if (!isOpen) return;

    // Response type for memory-aware assignment endpoint
    interface AssignmentWithMemory {
      attendee_name: string;
      section: 'PC' | 'EB' | null;
      section_source: 'stored' | 'memory' | null;
      memory_confidence: number | null;
    }

    const loadAssignments = async () => {
      setIsLoading(true);
      console.log('[TicketAssignment] Loading assignments with memory fallback for eventKey:', eventKey);
      try {
        // Fetch saved assignments from API with memory fallback
        const response = await apiClient.get<{
          event_key: string;
          assignments: AssignmentWithMemory[];
        }>(`/hmc/assignments/${encodeURIComponent(eventKey)}/with-memory`);

        console.log('[TicketAssignment] Loaded saved assignments:', response.data);

        // Build case-insensitive map of saved assignments (lowercase key -> { originalName, section })
        const savedAssignmentsMap = new Map<string, { originalName: string; section: 'PC' | 'EB' }>(
          response.data.assignments.map((a) => [
            a.attendee_name.toLowerCase().trim(),
            { originalName: a.attendee_name, section: a.section as 'PC' | 'EB' }
          ])
        );

        // Build a set of parsed attendee names (case-insensitive for matching)
        const parsedNamesLower = new Set(
          parsedAttendance.entries.map((e) => e.name.toLowerCase().trim())
        );

        // Merge parsed attendance with saved assignments (case-insensitive match)
        const mergedAssignments: Assignment[] = parsedAttendance.entries.map(
          (entry) => {
            const savedEntry = savedAssignmentsMap.get(entry.name.toLowerCase().trim());
            return {
              attendee_name: entry.name,
              section: savedEntry?.section || entry.section || 'unassigned',
              tickets: entry.tickets,
            };
          }
        );

        // Also include saved assignments that don't match any current attendees
        // (preserves assignments if attendee names changed slightly between reports)
        for (const [lowerName, savedEntry] of savedAssignmentsMap) {
          if (!parsedNamesLower.has(lowerName)) {
            console.log('[TicketAssignment] Preserving orphaned assignment:', savedEntry.originalName, savedEntry.section);
            mergedAssignments.push({
              attendee_name: savedEntry.originalName,
              section: savedEntry.section,
              tickets: 0, // Unknown ticket count - attendee not in current report
            });
          }
        }

        console.log('[TicketAssignment] Merged assignments:', mergedAssignments);
        setAssignments(mergedAssignments);
      } catch (error) {
        console.log('[TicketAssignment] No saved assignments found (or error), using parsed sections:', error);
        // If no saved assignments, use parsed sections
        const initialAssignments: Assignment[] = parsedAttendance.entries.map(
          (entry) => ({
            attendee_name: entry.name,
            section: entry.section || 'unassigned',
            tickets: entry.tickets,
          })
        );
        setAssignments(initialAssignments);
      } finally {
        setIsLoading(false);
      }
    };

    loadAssignments();
  }, [isOpen, eventKey, parsedAttendance.entries]);

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Group assignments by section
  const groupedAssignments = useMemo(() => {
    return {
      unassigned: assignments.filter((a) => a.section === 'unassigned'),
      PC: assignments.filter((a) => a.section === 'PC'),
      EB: assignments.filter((a) => a.section === 'EB'),
    };
  }, [assignments]);

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Check if dropped on a droppable section (PC, EB, unassigned)
    if (overId === 'PC' || overId === 'EB' || overId === 'unassigned') {
      setAssignments((prev) =>
        prev.map((a) =>
          a.attendee_name === activeId ? { ...a, section: overId } : a
        )
      );
      return;
    }

    // If dropped on another card, find which section that card is in
    const targetAssignment = assignments.find((a) => a.attendee_name === overId);
    if (targetAssignment) {
      setAssignments((prev) =>
        prev.map((a) =>
          a.attendee_name === activeId ? { ...a, section: targetAssignment.section } : a
        )
      );
    }
  }, [assignments]);

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Filter out unassigned - only save PC and EB assignments
      const assignmentsToSave = assignments
        .filter((a) => a.section !== 'unassigned')
        .map((a) => ({
          attendee_name: a.attendee_name,
          section: a.section,
        }));

      console.log('[TicketAssignment] Saving assignments:', {
        eventKey,
        count: assignmentsToSave.length,
        assignments: assignmentsToSave,
      });

      const response = await apiClient.put(`/hmc/assignments/${encodeURIComponent(eventKey)}`, {
        assignments: assignmentsToSave,
      });

      console.log('[TicketAssignment] Save response:', response.data);

      addNotification({
        type: 'info',
        title: 'Ticket Assignments Saved',
        message: `Saved ${assignmentsToSave.length} assignments for ${event.opponent || 'event'}`,
        severity: 'success',
      });

      onSave?.();
      onClose();
    } catch (error) {
      console.error('[TicketAssignment] Save error:', error);
      addNotification({
        type: 'warning',
        title: 'Ticket Assignment Save Failed',
        message: 'Failed to save assignments',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Get active assignment for drag overlay
  const activeAssignment = useMemo(() => {
    if (!activeId) return null;
    return assignments.find((a) => a.attendee_name === activeId) || null;
  }, [activeId, assignments]);

  // Handle mobile tap-to-move
  const handleMoveAssignment = useCallback((attendeeName: string, toSection: 'PC' | 'EB' | 'unassigned') => {
    setAssignments((prev) =>
      prev.map((a) =>
        a.attendee_name === attendeeName ? { ...a, section: toSection } : a
      )
    );
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal - Full screen on mobile */}
      <div className="relative bg-dark-bg border border-dark-border/30 rounded-xl w-full max-w-4xl h-[95vh] sm:h-auto sm:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-dark-border/30 shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-xl font-semibold text-white truncate">
              {isMobile ? 'Assign Sections' : 'Assign Attendees to Sections'}
            </h2>
            <p className="text-xs sm:text-sm text-dark-muted mt-0.5 truncate">
              {event.opponent} - {parseLocalDate(event.event_date)?.toLocaleDateString() ?? 'Date TBD'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-surface rounded-lg transition-all duration-150 shrink-0 ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <svg className="w-5 h-5 text-dark-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile instruction banner */}
        {isMobile && !isLoading && assignments.length > 0 && (
          <div className="px-3 py-2 bg-brand-500/10 border-b border-brand-500/20 shrink-0">
            <p className="text-xs text-brand-300 text-center">
              Tap PC or EB buttons to assign attendees
            </p>
          </div>
        )}

        {/* Content */}
        <div className="p-3 sm:p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-12 text-dark-muted">
              <p>No attendees found for this event.</p>
              <p className="text-sm mt-2">Upload a report with attendance data first.</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              {/* Mobile: Stack columns vertically, Desktop: horizontal */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-center">
                {/* Unassigned column */}
                <SectionColumn
                  title="Unassigned"
                  section="unassigned"
                  assignments={groupedAssignments.unassigned}
                  color="bg-dark-input"
                  isMobile={isMobile}
                  onMoveAssignment={handleMoveAssignment}
                />

                {/* PC column */}
                <SectionColumn
                  title="PC Section"
                  section="PC"
                  assignments={groupedAssignments.PC}
                  capacity={pcCapacity}
                  color="bg-purple-600"
                  isMobile={isMobile}
                  onMoveAssignment={handleMoveAssignment}
                />

                {/* EB column */}
                <SectionColumn
                  title="EB Section"
                  section="EB"
                  assignments={groupedAssignments.EB}
                  capacity={ebCapacity}
                  color="bg-brand-600"
                  isMobile={isMobile}
                  onMoveAssignment={handleMoveAssignment}
                />
              </div>

              {/* Drag overlay - only on desktop */}
              {!isMobile && (
                <DragOverlay>
                  {activeAssignment && (
                    <AttendeeCard assignment={activeAssignment} isDragging />
                  )}
                </DragOverlay>
              )}
            </DndContext>
          )}

          {/* Summary - Mobile: stacked, Desktop: inline */}
          {!isLoading && assignments.length > 0 && (
            <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-dark-border/30">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm">
                <div className="flex flex-wrap gap-2 sm:gap-4">
                  <span className="text-purple-400">
                    PC: {groupedAssignments.PC.reduce((sum, a) => sum + a.tickets, 0)}
                  </span>
                  <span className="text-blue-400">
                    EB: {groupedAssignments.EB.reduce((sum, a) => sum + a.tickets, 0)}
                  </span>
                  <span className="text-dark-muted">
                    Unassigned: {groupedAssignments.unassigned.reduce((sum, a) => sum + a.tickets, 0)}
                  </span>
                </div>
                <span className="text-white font-medium">
                  Total: {assignments.reduce((sum, a) => sum + a.tickets, 0)} tickets
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom on mobile */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 px-3 sm:px-6 py-3 sm:py-4 border-t border-dark-border/30 shrink-0 bg-dark-bg">
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-2 bg-dark-surface text-white rounded-lg hover:bg-dark-border transition-all duration-150 min-h-[44px] text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className={`
              px-3 sm:px-4 py-2 rounded-lg min-h-[44px] font-medium transition-all duration-150 text-sm sm:text-base
              ${isSaving || isLoading
                ? 'bg-brand-500/50 text-white/50 cursor-not-allowed'
                : 'bg-brand-500 text-white hover:bg-brand-600'
              }
            `}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketAssignmentModal;
